/**
 * @file index.jsx
 * @brief Renders the legacy full-screen messaging layout with thread, listing, and transaction workflows.
 */
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { useTranslation } from "react-i18next";
import { createApiClient } from "../../api/axios";
import { useUserProfile } from "../../context/UserProfileContext";
import { useDropzone } from "react-dropzone";
import { useProtectedImage, useProtectedImages } from "../../hooks/useProtectedImage";
import { useChatSocket } from "../../hooks/useChatSocket";
import { getTransactionErrorMessage } from "../../utils/transactionErrors";
import { clampToMaxLength, FIELD_LIMITS } from "../../constants/fieldLimits";
import { truncateText } from "../../utils/truncateText";
import { getUserDisplayName } from "../../utils/userDisplayName";
import styles from "./messages-layout.module.css";

const WS_BASE = process.env.REACT_APP_WS_BASE_URL || "ws://localhost:7000";

/* ══════════════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
══════════════════════════════════════════════════════════════════════════ */

/* ─── Image lightbox ─────────────────────────────────────────────────────── */

function ImageLightbox({ urls, initialIndex, onClose }) {
    const [index, setIndex] = useState(initialIndex);
    useEffect(() => {
        /**
         * Handles keyboard shortcuts for closing the lightbox and cycling between images.
         * @param {KeyboardEvent} e Browser keyboard event.
         * @returns {void}
         */
        const h = (e) => {
            if (e.key === "Escape")     onClose();
            if (e.key === "ArrowRight") setIndex((i) => (i + 1) % urls.length);
            if (e.key === "ArrowLeft")  setIndex((i) => (i - 1 + urls.length) % urls.length);
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [onClose, urls.length]);

    return (
        <div className={styles.lightboxOverlay} onClick={onClose}>
            <button className={styles.lightboxClose} onClick={onClose}>✕</button>
            {urls.length > 1 && (
                <button className={styles.lightboxPrev}
                    onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + urls.length) % urls.length); }}>‹</button>
            )}
            <img src={urls[index]} className={styles.lightboxImage} alt=""
                onClick={(e) => e.stopPropagation()} />
            {urls.length > 1 && (
                <button className={styles.lightboxNext}
                    onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % urls.length); }}>›</button>
            )}
            {urls.length > 1 && (
                <div className={styles.lightboxDots}>
                    {urls.map((_, i) => (
                        <button key={i}
                            className={`${styles.lightboxDot} ${i === index ? styles.lightboxDotActive : ""}`}
                            onClick={(e) => { e.stopPropagation(); setIndex(i); }} />
                    ))}
                </div>
            )}
        </div>
    );
}

/* ─── Message bubble ─────────────────────────────────────────────────────── */

function MessageBubble({ message, myId }) {
    const blobUrls = useProtectedImages(message.images ?? []);
    const isMine   = message.sender?.id === myId;
    const [lightboxIndex, setLightboxIndex] = useState(null);

    const rowClass = message.is_system
        ? `${styles.bubbleRow} ${styles.bubbleRowSystem}`
        : `${styles.bubbleRow} ${isMine ? styles.bubbleRowMine : styles.bubbleRowTheirs}`;

    const bubbleClass = message.is_system
        ? `${styles.bubble} ${styles.bubbleSystem}`
        : `${styles.bubble} ${isMine ? styles.bubbleMine : styles.bubbleTheirs}`;

    return (
        <div className={rowClass}>
            <div className={bubbleClass}>
                {message.content && <p className={styles.bubbleText}>{message.content}</p>}
                {!message.is_system && blobUrls.length > 0 && (
                    <div className={styles.bubbleImages}>
                        {blobUrls.map((url, i) => url && (
                            <img key={i} src={url} alt="" className={styles.bubbleImage}
                                onClick={() => setLightboxIndex(i)} />
                        ))}
                    </div>
                )}
                <span className={styles.bubbleTime}>
                    {new Date(message.sent_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {!message.is_system && isMine && <span className={styles.readStatus}>{message.read ? " ✓✓" : " ✓"}</span>}
                </span>
            </div>
            {!message.is_system && lightboxIndex !== null && (
                <ImageLightbox urls={blobUrls.filter(Boolean)} initialIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)} />
            )}
        </div>
    );
}

/**
 * Shows the current transaction state for the active conversation and exposes the valid actions.
 * @param {Object} props Component props.
 * @param {?Object} props.transaction Active transaction for the thread.
 * @param {string|number} props.myId Signed-in user identifier.
 * @param {Function} props.onAccept Accepts a pending transaction.
 * @param {Function} props.onRefuse Refuses a pending transaction.
 * @param {Function} props.onConfirm Confirms completion for an accepted transaction.
 * @param {boolean} props.busy Indicates whether a transaction action is in flight.
 * @param {?string} props.error Localized error message to display.
 * @returns {JSX.Element | null}
 */
function TransactionBanner({ transaction, myId, onAccept, onRefuse, onConfirm, busy, error }) {
    const { t } = useTranslation();

    if (!transaction) return null;

    const isSeller = transaction.seller?.id === myId;
    const isBuyer = transaction.buyer?.id === myId;

    return (
        <div className={styles.transactionBanner}>
            <div className={styles.transactionText}>
                <strong>{t("messages.transaction.title")}</strong>
                <span>
                    {t("messages.transaction.summary", {
                        kind: t(`messages.transaction.kinds.${transaction.kind}`),
                        offeredPrice: transaction.offered_price,
                        status: t(`messages.transaction.statuses.${transaction.status}`),
                    })}
                </span>
                {error && <span className={styles.transactionError}>{error}</span>}
            </div>

            <div className={styles.transactionActions}>
                {isSeller && transaction.status === "pending" && (
                    <>
                        <button className={styles.txAcceptBtn} onClick={onAccept} disabled={busy}>{t("messages.transaction.accept")}</button>
                        <button className={styles.txRefuseBtn} onClick={onRefuse} disabled={busy}>{t("messages.transaction.refuse")}</button>
                    </>
                )}

                {isBuyer && transaction.status === "accepted" && (
                    <button className={styles.txConfirmBtn} onClick={onConfirm} disabled={busy}>
                        {t("messages.transaction.confirmItemOk")}
                    </button>
                )}
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════════════════
   THREAD PANEL
   Rendered in the centre when a conversation is selected.
══════════════════════════════════════════════════════════════════════════ */

const PAGE_SIZE = 20;
const AUTO_SCROLL_THRESHOLD = 80;

/**
 * Compares conversation list snapshots to avoid unnecessary sidebar updates.
 * @param {Array<Object>} a Previous conversation list.
 * @param {Array<Object>} b Next conversation list.
 * @returns {boolean}
 */
function areConversationListsEqual(a = [], b = []) {
    if (a === b) return true;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i += 1) {
        const left = a[i] || {};
        const right = b[i] || {};

        if (String(left.listing_id ?? "") !== String(right.listing_id ?? "")) return false;
        if (String(left.other_user?.id ?? "") !== String(right.other_user?.id ?? "")) return false;
        if ((left.unread_count || 0) !== (right.unread_count || 0)) return false;
        if ((left.last_message || "") !== (right.last_message || "")) return false;
        if ((left.last_message_at || "") !== (right.last_message_at || "")) return false;
    }

    return true;
}

function isValidId(value) {
    if (value === null || value === undefined) return false;
    const asString = String(value).trim().toLowerCase();
    return asString !== "" && asString !== "null" && asString !== "undefined";
}

function ThreadPanel({
    listingId,
    otherUserId,
    api,
    myId,
    onBack,
    onListingLoad,
    onConversationMessage,
    onConversationRead,
    dropRef,
    isDragActive,
}) {
    const auth = useAuth();
    const { t } = useTranslation();

    const [messages, setMessages]       = useState([]);
    const [hasMore, setHasMore]         = useState(false);
    const [offset, setOffset]           = useState(0);
    const [listing, setListing]         = useState(null);
    const [otherUser, setOtherUser]     = useState(null);
    const [loading, setLoading]         = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [sending, setSending]         = useState(false);
    const [txBusy, setTxBusy]           = useState(false);
    const [txError, setTxError]         = useState(null);
    const [txCreateBusy, setTxCreateBusy] = useState(false);
    const [txCreateError, setTxCreateError] = useState(null);
    const [offerPrice, setOfferPrice]   = useState("");
    const [content, setContent]         = useState("");
    const [attachments, setAttachments] = useState([]);
    const [transaction, setTransaction] = useState(null);
    const headerAvatarUrl = useProtectedImage(otherUser?.profile_picture_url || null);

    const bottomRef      = useRef(null);
    const topSentinel    = useRef(null);
    const messageAreaRef = useRef(null);
    const loadingMoreRef = useRef(false);
    const hasMoreRef     = useRef(false);
    const offsetRef      = useRef(0);
    const autoScrollPendingRef = useRef(false);

    useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
    useEffect(() => { offsetRef.current  = offset;  }, [offset]);

    const isNearBottom = useCallback(() => {
        const area = messageAreaRef.current;
        if (!area) return true;
        const distance = area.scrollHeight - area.scrollTop - area.clientHeight;
        return distance <= AUTO_SCROLL_THRESHOLD;
    }, []);

    /* ── Core fetch ── */
    const fetchPage = useCallback(async (pageOffset) => {
        const res = await api.get("messages/", {
            params: { listing: listingId, other_user: otherUserId, limit: PAGE_SIZE, offset: pageOffset },
        });
        const data    = res.data;
        const results = data.results ?? data;
        const count   = data.count   ?? results.length;
        return { results, count };
    }, [api, listingId, otherUserId]);

    const loadTransaction = useCallback(async () => {
        const res = await api.get("transactions/", {
            params: {
                listing: listingId,
                counterparty: otherUserId,
            },
        });

        const list = res.data?.results ?? res.data;
        setTransaction(list?.[0] ?? null);
    }, [api, listingId, otherUserId]);

    /* ── WebSocket ── */
    const { sendMessage: wsSend, markRead: wsMarkRead, connected: wsConnected } = useChatSocket({
        listingId,
        otherUserId,
        token: auth.user?.access_token,
        onMessage: (msg) => {
            const shouldStickToBottom = isNearBottom();
            const incomingFromOther = String(msg.sender?.id) !== String(myId);

            // New message pushed from server — prepend to newest-first array
            setMessages((prev) => {
                if (prev.find((m) => m.id === msg.id)) return prev; // dedup
                return [msg, ...prev];
            });

            // Thread is open: mark incoming messages as read immediately.
            if (incomingFromOther && shouldStickToBottom) {
                acknowledgeRead();
            }

            if (shouldStickToBottom) {
                autoScrollPendingRef.current = true;
            }

            onConversationMessage?.(msg, { isReadingLatest: shouldStickToBottom });
        },
        onRead: (event) => {
            // The other person read our messages — flip read flag
            setMessages((prev) =>
                prev.map((m) =>
                    m.sender?.id === myId && !m.read
                        ? { ...m, read: true }
                        : m
                )
            );

            onConversationRead?.(event);
        },
        onTransaction: async (event) => {
            const tx = event?.transaction;
            if (!tx) return;

            setTransaction(tx);
            setTxError(null);

            try {
                const refreshedListing = await api.get(`listings/${listingId}/`);
                setListing(refreshedListing.data);
                onListingLoad?.(refreshedListing.data);
            } catch {
                // Ignore transient listing refresh failures; transaction state already updated.
            }
        },
    });

    const acknowledgeRead = useCallback(() => {
        if (!listingId || !otherUserId) return;

        const readEvent = { reader_id: myId, listing_id: listingId };

        if (wsConnected) {
            wsMarkRead();
            onConversationRead?.(readEvent);
            return;
        }

        // Fallback when WS is disconnected.
        api.post("messages/mark-read/", { listing: listingId, other_user: otherUserId })
            .then(() => onConversationRead?.(readEvent))
            .catch(() => {});
    }, [api, listingId, myId, onConversationRead, otherUserId, wsConnected, wsMarkRead]);

    /* ── Init ── */
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            setLoading(true);
            setMessages([]);
            setOffset(0);
            offsetRef.current = 0;
            try {
                const [listingRes, userRes] = await Promise.all([
                    api.get(`listings/${listingId}/`),
                    api.get(`userprofiles/${otherUserId}/`),
                ]);
                if (cancelled) return;
                setListing(listingRes.data);
                onListingLoad?.(listingRes.data);
                setOtherUser(userRes.data);
                const { results, count } = await fetchPage(0);
                if (cancelled) return;
                setMessages(results);
                setOffset(PAGE_SIZE);
                offsetRef.current = PAGE_SIZE;
                setHasMore(count > PAGE_SIZE);
                await loadTransaction();
                await api.post("messages/mark-read/", { listing: listingId, other_user: otherUserId }).catch(() => {});
                onConversationRead?.({ reader_id: myId, listing_id: listingId });
            } catch (err) {
                console.error(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        init();
        return () => {
            cancelled = true;
            // Reset loading for the next mount so the observer effect re-runs
            setLoading(true);
        };
    }, [api, fetchPage, listingId, loadTransaction, myId, onConversationRead, onListingLoad, otherUserId]);

    /* ── Scroll to bottom on init ── */
    const scrolledOnInit = useRef(false);
    useEffect(() => {
        scrolledOnInit.current = false;
    }, [listingId, otherUserId]);

    useEffect(() => {
        if (!loading && !scrolledOnInit.current && messages.length > 0) {
            bottomRef.current?.scrollIntoView({ behavior: "instant" });
            scrolledOnInit.current = true;
        }
    }, [loading, messages.length]);

    // When user scrolls back to the latest messages, mark thread as read.
    useEffect(() => {
        const area = messageAreaRef.current;
        if (!area) return;

        /**
         * Marks the thread as read when the user scrolls back to the newest messages.
         * @returns {void}
         */
        const handleScroll = () => {
            if (!isNearBottom()) return;
            const hasUnreadIncoming = messages.some(
                (m) => String(m.sender?.id) !== String(myId) && !m.read
            );
            if (hasUnreadIncoming) acknowledgeRead();
        };

        area.addEventListener("scroll", handleScroll, { passive: true });
        return () => area.removeEventListener("scroll", handleScroll);
    }, [acknowledgeRead, isNearBottom, messages, myId]);

    // Only auto-scroll after new messages render, and only when user was
    // already near the bottom before those messages arrived.
    useEffect(() => {
        if (!autoScrollPendingRef.current) return;
        autoScrollPendingRef.current = false;
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length]);

    /* ── Fallback poll — only runs when WebSocket is disconnected ── */
    useEffect(() => {
        if (wsConnected) return; // WS handles updates when connected
        const interval = setInterval(async () => {
            try {
                const shouldStickToBottom = isNearBottom();
                const { results } = await fetchPage(0);

                let freshCount = 0;
                let hasIncomingFromOther = false;
                let freshMessages = [];

                setMessages((prev) => {
                    const ids   = new Set(prev.map((m) => m.id));
                    const fresh = results.filter((m) => !ids.has(m.id));
                    freshMessages = fresh;
                    freshCount = fresh.length;
                    hasIncomingFromOther = fresh.some((m) => String(m.sender?.id) !== String(myId));
                    return fresh.length > 0 ? [...fresh, ...prev] : prev;
                });

                freshMessages.forEach((m) => onConversationMessage?.(m, { isReadingLatest: shouldStickToBottom }));

                if (hasIncomingFromOther && shouldStickToBottom) acknowledgeRead();

                if (shouldStickToBottom && freshCount > 0) {
                    autoScrollPendingRef.current = true;
                }

                await loadTransaction();
            } catch (err) { console.error(err); }
        }, 10_000);
        return () => clearInterval(interval);
    }, [acknowledgeRead, fetchPage, isNearBottom, loadTransaction, myId, onConversationMessage, wsConnected]);

    const respondToTransaction = async (decision) => {
        if (!transaction) return;
        setTxBusy(true);
        setTxError(null);
        try {
            await api.post(`transactions/${transaction.id}/respond/`, { decision });
            const refreshedListing = await api.get(`listings/${listingId}/`);
            setListing(refreshedListing.data);
            onListingLoad?.(refreshedListing.data);
            await loadTransaction();
        } catch (err) {
            setTxError(getTransactionErrorMessage(t, err, "messages.transaction.actionError"));
            console.error("Failed to respond to transaction", err);
        } finally {
            setTxBusy(false);
        }
    };

    const confirmComplete = async () => {
        if (!transaction) return;
        setTxBusy(true);
        setTxError(null);
        try {
            await api.post(`transactions/${transaction.id}/confirm-complete/`);
            const refreshedListing = await api.get(`listings/${listingId}/`);
            setListing(refreshedListing.data);
            onListingLoad?.(refreshedListing.data);
            await loadTransaction();
        } catch (err) {
            setTxError(getTransactionErrorMessage(t, err, "messages.transaction.actionError"));
            console.error("Failed to complete transaction", err);
        } finally {
            setTxBusy(false);
        }
    };

    const createTransaction = async (kind) => {
        if (!listing) return;

        setTxCreateBusy(true);
        setTxCreateError(null);

        try {
            const payload = {
                listing: listing.id,
                kind,
            };

            if (kind === "offer") {
                const parsed = Number(offerPrice);
                if (!Number.isFinite(parsed) || parsed <= 0) {
                    setTxCreateError(t("contactSeller.offerInvalid"));
                    return;
                }
                payload.offered_price = parsed.toFixed(2);
            }

            await api.post("transactions/", payload);
            setOfferPrice("");

            const [{ results }, refreshedListing] = await Promise.all([
                fetchPage(0),
                api.get(`listings/${listingId}/`),
            ]);

            setMessages(results);
            setListing(refreshedListing.data);
            onListingLoad?.(refreshedListing.data);

            await loadTransaction();
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        } catch (err) {
            setTxCreateError(getTransactionErrorMessage(t, err, "contactSeller.transactionCreateError"));
            console.error("Failed to create transaction from thread", err);
        } finally {
            setTxCreateBusy(false);
        }
    };

    const canCreateTransaction =
        Boolean(listing) &&
        String(listing?.author?.id) !== String(myId) &&
        listing?.status === "active" &&
        (!transaction || transaction.status === "refused");

    /* ── Infinite scroll — exact same pattern as the working ConversationThread ── */
    useEffect(() => {
        if (loading) return;
        const sentinel = topSentinel.current;
        const area     = messageAreaRef.current;
        if (!sentinel || !area) return;

        let initialFired = false;

        const observer = new IntersectionObserver(
            async ([entry]) => {
                if (!initialFired) { initialFired = true; return; }
                if (!entry.isIntersecting || loadingMoreRef.current || !hasMoreRef.current) return;

                loadingMoreRef.current = true;
                setLoadingMore(true);
                const prevHeight = area.scrollHeight;

                try {
                    const { results, count } = await fetchPage(offsetRef.current);
                    setMessages((prev) => {
                        const ids   = new Set(prev.map((m) => m.id));
                        const older = results.filter((m) => !ids.has(m.id));
                        return older.length > 0 ? [...prev, ...older] : prev;
                    });
                    const newOffset = offsetRef.current + PAGE_SIZE;
                    offsetRef.current = newOffset;
                    setOffset(newOffset);
                    setHasMore(newOffset < count);
                    requestAnimationFrame(() => { area.scrollTop = area.scrollHeight - prevHeight; });
                } catch (err) {
                    console.error(err);
                } finally {
                    loadingMoreRef.current = false;
                    setLoadingMore(false);
                }
            },
            { root: area, threshold: 0 }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, fetchPage]);

    /* ── Dropzone — handleFileDrop adds to local state; registered via dropRef ── */
    const handleFileDrop = useCallback((files) => {
        setAttachments((prev) => [...prev, ...files.map((f) => ({
            id: crypto.randomUUID(), file: f, preview: URL.createObjectURL(f),
        }))]);
    }, []);

    // Register with parent so drag-drop on .chatColumn works
    useEffect(() => {
        if (dropRef) dropRef.current = handleFileDrop;
        return () => { if (dropRef) dropRef.current = null; };
    }, [dropRef, handleFileDrop]);

    const removeAttachment = (id) =>
        setAttachments((prev) => {
            const a = prev.find((x) => x.id === id);
            if (a) URL.revokeObjectURL(a.preview);
            return prev.filter((x) => x.id !== id);
        });

    /* ── Send ── */
    const handleSend = async () => {
        if (!content.trim() && attachments.length === 0) return;
        if (content.trim().length > FIELD_LIMITS.messageContent) return;

        setSending(true);
        const text = content.trim();
        setContent("");

        try {
            if (attachments.length === 0 && wsSend(text)) {
                // Sent via WebSocket — server will broadcast back to us via onMessage
                setAttachments([]);
            } else {
                // Fall back to REST (always used for image attachments)
                const formData = new FormData();
                formData.append("listing", listingId);
                formData.append("content", text);
                formData.append("receiver_id", otherUserId);
                attachments.forEach((a) => formData.append("new_images", a.file));
                await api.post("messages/", formData, { headers: { "Content-Type": "multipart/form-data" } });
                setAttachments([]);
                // Fetch the saved message so we have the server-generated data
                const { results } = await fetchPage(0);
                setMessages((prev) => {
                    const ids   = new Set(prev.map((m) => m.id));
                    const fresh = results.filter((m) => !ids.has(m.id));
                    return fresh.length > 0 ? [...fresh, ...prev] : prev;
                });
            }
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
        } catch (err) {
            console.error("Send failed", err);
            setContent(text); // restore on failure
        } finally {
            setSending(false);
        }
    };

    /**
     * Sends the draft message when Enter is pressed without Shift.
     * @param {KeyboardEvent} e Textarea keyboard event.
     * @returns {void}
     */
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    const displayMessages = [...messages].reverse();
    const grouped = displayMessages.reduce((acc, msg) => {
        const day = new Date(msg.sent_at).toLocaleDateString();
        if (!acc[day]) acc[day] = [];
        acc[day].push(msg);
        return acc;
    }, {});
    const otherUserName = getUserDisplayName(otherUser?.user);

    return (
        <>
            {isDragActive && (
                <div className={styles.dragOverlay}><span>📎 {t("messages.dropImages")}</span></div>
            )}

            {/* Connection indicator */}
            {!wsConnected && !loading && (
                <div className={styles.wsIndicator}>
                    <span className={styles.wsIndicatorDot} /> {t("messages.reconnecting")}
                </div>
            )}

            {/* Loading overlay — keeps messageArea in the DOM so refs are always set */}
            {loading && (
                <div className={styles.chatLoading}>
                    <div className={styles.spinner} />
                    <p>{t("messages.loading")}</p>
                </div>
            )}

            {/* Header — hidden while loading but still mounted */}
            <div className={styles.threadHeader} style={{ display: loading ? "none" : "" }}>
                {/* Back button — mobile only */}
                <button className={styles.backBtn} onClick={onBack}>←</button>
                <div className={styles.headerInfo}>
                    <div className={styles.headerAvatar}>
                        {headerAvatarUrl ? (
                            <img
                                src={headerAvatarUrl}
                                alt={`${otherUserName.primaryName} avatar`}
                                className={styles.headerAvatarImage}
                            />
                        ) : (
                            otherUserName.avatarInitial
                        )}
                    </div>
                    <div>
                        <p className={styles.headerName}>{otherUserName.primaryName}</p>
                        {otherUserName.secondaryName && (
                            <p className={styles.headerUsername}>{otherUserName.secondaryName}</p>
                        )}
                        {listing && (
                            <Link to={`/listings/${listing.id}`} className={styles.headerListing} title={listing.title}>
                                🏷 {truncateText(listing.title)}
                            </Link>
                        )}
                    </div>
                </div>
            </div>

            <div style={{ display: loading ? "none" : "" }}>
                <TransactionBanner
                    transaction={transaction}
                    myId={myId}
                    onAccept={() => respondToTransaction("accept")}
                    onRefuse={() => respondToTransaction("refuse")}
                    onConfirm={confirmComplete}
                    busy={txBusy}
                    error={txError}
                />
            </div>

            {canCreateTransaction && (
                <div className={styles.transactionComposer} style={{ display: loading ? "none" : "" }}>
                    <button
                        className={styles.buyBtn}
                        onClick={() => createTransaction("buy")}
                        disabled={txCreateBusy}
                    >
                        {txCreateBusy
                            ? t("contactSeller.sending")
                            : t("contactSeller.buyNow", { price: listing.price })}
                    </button>

                    <div className={styles.offerRow}>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={offerPrice}
                            onChange={(e) => setOfferPrice(e.target.value)}
                            placeholder={t("contactSeller.offerPlaceholder")}
                            className={styles.offerInput}
                            disabled={txCreateBusy}
                        />
                        <button
                            className={styles.offerBtn}
                            onClick={() => createTransaction("offer")}
                            disabled={txCreateBusy}
                        >
                            {t("contactSeller.sendOffer")}
                        </button>
                    </div>

                    {txCreateError && <span className={styles.transactionError}>{txCreateError}</span>}
                </div>
            )}

            {/* Messages */}
            <div className={styles.messageArea} ref={messageAreaRef} style={{ display: loading ? "none" : "" }}>
                <div ref={topSentinel} className={styles.topSentinel} />
                {loadingMore && (
                    <div className={styles.loadingMore}><span className={styles.loadingMoreSpinner} /></div>
                )}
                {displayMessages.length === 0 ? (
                    <div className={styles.emptyThread}>
                        <span>👋</span>
                        <p>{t("messages.startConversation")}</p>
                    </div>
                ) : (
                    Object.entries(grouped).map(([day, msgs]) => (
                        <div key={day}>
                            <div className={styles.dateDivider}><span>{day}</span></div>
                            {msgs.map((msg) => (
                                <MessageBubble key={msg.id} message={msg} myId={myId} />
                            ))}
                        </div>
                    ))
                )}
                <div ref={bottomRef} />
            </div>

            {/* Attachments */}
            {attachments.length > 0 && (
                <div className={styles.attachmentBar}>
                    {attachments.map((a) => (
                        <div key={a.id} className={styles.attachThumb}>
                            <img src={a.preview} alt="" />
                            <button className={styles.attachRemove}
                                onClick={() => removeAttachment(a.id)}>✕</button>
                        </div>
                    ))}
                </div>
            )}

            {/* Composer */}
            <div className={styles.composer} style={{ display: loading ? "none" : "" }}>
                <label className={styles.attachBtn} title={t("messages.attachImages")}>
                    📎
                    <input type="file" accept="image/*" multiple hidden
                        onChange={(e) => handleFileDrop(Array.from(e.target.files))} />
                </label>
                <textarea
                    className={styles.composerInput}
                    placeholder={t("messages.writeMessage")}
                    value={content}
                    onChange={(e) => setContent(clampToMaxLength(e.target.value, FIELD_LIMITS.messageContent))}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    maxLength={FIELD_LIMITS.messageContent}
                />
                <button className={styles.sendBtn} onClick={handleSend}
                    disabled={sending || (!content.trim() && attachments.length === 0)}>
                    {sending ? <span className={styles.btnSpinner} /> : "➤"}
                </button>
            </div>
        </>
    );
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN LAYOUT
   Route: /messages  and  /messages/:listingId/:otherUserId
   Both use this single component. The URL params determine what's open.
══════════════════════════════════════════════════════════════════════════ */

export default function MessagesLayout() {
    const { listingId, otherUserId } = useParams();   // undefined on /messages
    const navigate                   = useNavigate();
    const auth                       = useAuth();
    const { t, i18n }                = useTranslation();
    const accessToken                = auth.user?.access_token;
    const isAuthenticated            = auth.isAuthenticated;
    const api                        = useMemo(
        () => createApiClient({
            isAuthenticated,
            user: accessToken ? { access_token: accessToken } : null,
        }),
        [accessToken, isAuthenticated]
    );
    const { profile }                = useUserProfile();
    const locale = i18n.resolvedLanguage?.startsWith("fr") ? "fr-FR" : "en-US";

    const [conversations, setConversations] = useState([]);
    const [convLoading, setConvLoading]     = useState(true);
    const [search, setSearch]               = useState("");
    const [activeListing, setActiveListing] = useState(null);

    /* ── Dropzone — lifted here so getRootProps can go on .chatColumn ── */
    // threadDropRef lets the parent's drag handler call into ThreadPanel's local state setter
    const threadDropRef = useRef(null);
    const handleDrop = useCallback((files) => {
        threadDropRef.current?.(files);
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDrop, accept: { "image/*": [] }, noClick: true,
    });

    const myId         = profile?.id;
    const currentListingId = isValidId(listingId) ? listingId : null;
    const currentOtherUserId = isValidId(otherUserId) ? otherUserId : null;
    const hasThread    = Boolean(currentListingId && currentOtherUserId);
    // On mobile, show sidebar when no thread is open
    const showSidebar  = !hasThread;

    /* ── Fetch conversations ── */
    const loadConversations = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setConvLoading(true);
        try {
            const res = await api.get("messages/conversations/");
            const incoming = res.data || [];
            setConversations((prev) => (areConversationListsEqual(prev, incoming) ? prev : incoming));
        } catch (err) { console.error(err); }
        finally {
            if (!silent) setConvLoading(false);
        }
    }, [api]);

    useEffect(() => {
        if (auth.user?.access_token) loadConversations();
    }, [auth.user?.access_token, loadConversations]);

    // Periodic silent refresh so brand-new conversations appear without reload.
    useEffect(() => {
        if (!auth.user?.access_token) return;

        const interval = setInterval(() => {
            loadConversations({ silent: true });
        }, 10_000);

        return () => clearInterval(interval);
    }, [auth.user?.access_token, loadConversations]);

    // Keep sidebar stable when switching threads; only clear local unread badge.
    useEffect(() => {
        if (!hasThread) {
            setActiveListing(null);
            return;
        }

        setConversations((prev) =>
            prev.map((conversation) => (
                String(conversation.listing_id) === String(listingId) &&
                String(conversation.other_user?.id) === String(currentOtherUserId)
                    ? { ...conversation, unread_count: 0 }
                    : conversation
            ))
        );
    }, [currentOtherUserId, hasThread, listingId]);

    const handleConversationMessage = useCallback((msg, { isReadingLatest = true } = {}) => {
        if (!msg) return;

        const messageListingId = String(msg.listing ?? listingId ?? "");
        const messageOtherUserId = String(
            String(msg.sender?.id) === String(myId)
                ? (msg.receiver?.id ?? "")
                : (msg.sender?.id ?? "")
        );
        if (!messageListingId || !messageOtherUserId) return;

        const isActiveConversation =
            String(currentListingId) === messageListingId &&
            String(currentOtherUserId) === messageOtherUserId;
        const incomingFromOther = String(msg.sender?.id) !== String(myId);

        setConversations((prev) => {
            const index = prev.findIndex(
                (c) =>
                    String(c.listing_id) === messageListingId &&
                    String(c.other_user?.id) === messageOtherUserId
            );

            const next = [...prev];

            if (index === -1) {
                const otherProfile = String(msg.sender?.id) === String(myId) ? msg.receiver : msg.sender;
                if (!otherProfile || !isValidId(otherProfile.id)) return prev;

                const listingTitle =
                    msg.listing_data?.title ??
                    activeListing?.title ??
                    t("messages.fallbackListing", { id: messageListingId });

                next.unshift({
                    listing_id: messageListingId,
                    listing_title: listingTitle,
                    listing_price: String(msg.listing_data?.price ?? ""),
                    other_user: otherProfile,
                    last_message: msg.content ?? "",
                    last_message_at: msg.sent_at ?? new Date().toISOString(),
                    unread_count: incomingFromOther && (!isActiveConversation || !isReadingLatest) ? 1 : 0,
                });
                return next;
            }

            const current = next[index];
            const nextUnread = incomingFromOther
                ? (isActiveConversation
                    ? (isReadingLatest ? 0 : (current.unread_count || 0) + 1)
                    : (current.unread_count || 0) + 1)
                : (isActiveConversation ? 0 : (current.unread_count || 0));

            next[index] = {
                ...current,
                last_message: msg.content ?? current.last_message,
                last_message_at: msg.sent_at ?? new Date().toISOString(),
                unread_count: nextUnread,
            };

            const [updated] = next.splice(index, 1);
            next.unshift(updated);
            return next;
        });
    }, [activeListing?.title, currentListingId, currentOtherUserId, listingId, myId, t]);

    const handleConversationRead = useCallback(({ reader_id, listing_id }) => {
        if (String(reader_id) !== String(myId)) return;

        const activeListingId = String(listing_id ?? currentListingId ?? "");
        if (!activeListingId || !currentOtherUserId) return;

        setConversations((prev) =>
            prev.map((c) => (
                String(c.listing_id) === activeListingId &&
                String(c.other_user?.id) === String(currentOtherUserId)
                    ? { ...c, unread_count: 0 }
                    : c
            ))
        );
    }, [currentListingId, currentOtherUserId, myId]);

    // Keep lightweight WS listeners for non-active conversations so sidebar unread
    // badges update in real time even while a different thread is open.
    const backgroundSocketsRef = useRef(new Map());
    const onConversationMessageRef = useRef(handleConversationMessage);
    const onConversationReadRef = useRef(handleConversationRead);

    useEffect(() => {
        onConversationMessageRef.current = handleConversationMessage;
    }, [handleConversationMessage]);

    useEffect(() => {
        onConversationReadRef.current = handleConversationRead;
    }, [handleConversationRead]);

    useEffect(() => {
        const token = auth.user?.access_token;
        const sockets = backgroundSocketsRef.current;

        if (!token) {
            sockets.forEach((ws) => ws.close());
            sockets.clear();
            return;
        }

        const activeKey = (currentListingId && currentOtherUserId)
            ? `${currentListingId}-${currentOtherUserId}`
            : null;

        const desiredKeys = new Set(
            conversations
                .filter((conv) => isValidId(conv?.listing_id) && isValidId(conv?.other_user?.id))
                .map((conv) => `${conv.listing_id}-${conv.other_user.id}`)
                .filter((key) => key !== activeKey)
        );

        // Close sockets no longer needed (or now active in the thread panel).
        sockets.forEach((ws, key) => {
            if (!desiredKeys.has(key)) {
                ws.close();
                sockets.delete(key);
            }
        });

        // Open sockets for all non-active conversations not yet subscribed.
        conversations.forEach((conv) => {
            const listing = conv?.listing_id;
            const other = conv?.other_user?.id;
            if (!isValidId(listing) || !isValidId(other)) return;

            const key = `${listing}-${other}`;
            if (key === activeKey || sockets.has(key)) return;

            const ws = new WebSocket(`${WS_BASE}/ws/messages/${listing}/${other}/?token=${token}`);
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === "chat.message") onConversationMessageRef.current?.(data.message);
                    if (data.type === "chat.read") onConversationReadRef.current?.(data);
                } catch (err) {
                    console.warn("[WS/bg] Failed to parse message", err);
                }
            };

            ws.onerror = () => {
                // Keep errors silent; active thread WS already exposes connection status.
            };

            ws.onclose = () => {
                sockets.delete(key);
            };

            sockets.set(key, ws);
        });

    }, [auth.user?.access_token, conversations, currentListingId, currentOtherUserId]);

    useEffect(() => {
        const sockets = backgroundSocketsRef.current;

        return () => {
            sockets.forEach((ws) => ws.close());
            sockets.clear();
        };
    }, []);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return conversations.filter((c) => {
            return (
                c.listing_title?.toLowerCase().includes(q) ||
                getUserDisplayName(c.other_user?.user).searchText.includes(q)
            );
        });
    }, [conversations, search]);
    const avatarSourceUrls = useMemo(
        () => filtered.map((conversation) => conversation.other_user?.profile_picture_url || null),
        [filtered]
    );
    const avatarUrls = useProtectedImages(avatarSourceUrls);

    return (
        <div className={styles.shell}>

            {/* ── Sidebar ── */}
            <aside className={`${styles.sidebar} ${showSidebar ? styles.sidebarVisible : ""}`}>
                <div className={styles.sidebarHeader}>
                    <h2 className={styles.sidebarTitle}>{t("messages.title")}</h2>
                </div>

                <div className={styles.sidebarSearch}>
                    <span className={styles.sidebarSearchIcon}>🔎</span>
                    <input
                        className={styles.sidebarSearchInput}
                        placeholder={t("messages.search")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className={styles.sidebarList}>
                    {convLoading ? (
                        [...Array(4)].map((_, i) => (
                            <div key={i} className={styles.skeletonItem}>
                                <div className={styles.skeletonAvatar} />
                                <div className={styles.skeletonBody}>
                                    <div className={styles.skeletonLine} style={{ width: "60%" }} />
                                    <div className={styles.skeletonLine} style={{ width: "38%" }} />
                                </div>
                            </div>
                        ))
                    ) : filtered.length === 0 ? (
                        <div className={styles.sidebarEmpty}>
                            <span>💬</span>
                            <p>{search ? t("messages.noResults") : t("messages.noConversations")}</p>
                        </div>
                    ) : (
                        filtered.map((conv, index) => {
                            const other    = conv.other_user;
                            if (!isValidId(conv.listing_id) || !isValidId(other?.id)) {
                                return null;
                            }
                            const otherName = getUserDisplayName(other?.user);
                            const isActive = String(conv.listing_id) === String(currentListingId)
                                          && String(other?.id)       === String(currentOtherUserId);
                            const unread   = conv.unread_count > 0;
                            const initial  = otherName.avatarInitial;
                            const avatarUrl = avatarUrls[index];

                            return (
                                <Link
                                    key={`${conv.listing_id}-${other?.id}`}
                                    to={`/messages/${conv.listing_id}/${other?.id}`}
                                    className={`${styles.item} ${isActive ? styles.itemActive : ""} ${unread ? styles.itemUnread : ""}`}
                                >
                                    <div className={styles.avatar}>
                                        {initial}
                                        {avatarUrl ? (
                                            <img
                                                src={avatarUrl}
                                                alt={`${otherName.primaryName} avatar`}
                                                className={styles.avatarImage}
                                                onError={(event) => {
                                                    event.currentTarget.remove();
                                                }}
                                            />
                                        ) : null}
                                    </div>
                                    <div className={styles.itemBody}>
                                        <div className={styles.itemTop}>
                                            <span className={styles.username}>{otherName.primaryName}</span>
                                            <span className={styles.time}>
                                                {conv.last_message_at
                                                    ? new Date(conv.last_message_at).toLocaleDateString(locale)
                                                    : ""}
                                            </span>
                                        </div>
                                        {otherName.secondaryName && (
                                            <p className={styles.usernameSecondary}>{otherName.secondaryName}</p>
                                        )}
                                        <div className={styles.itemBottom}>
                                            <span className={styles.listingName} title={conv.listing_title}>🏷 {truncateText(conv.listing_title)}</span>
                                            {unread && <span className={styles.unreadBadge}>{conv.unread_count}</span>}
                                        </div>
                                        {conv.last_message && (
                                            <p className={styles.preview}>{conv.last_message}</p>
                                        )}
                                    </div>
                                </Link>
                            );
                        })
                    )}
                </div>
            </aside>

            {/* ── Main area ── */}
            <div className={`${styles.main} ${hasThread ? styles.mainWithThread : ""}`}>
                {hasThread ? (
                    /* Thread + listing panel rendered as flex children of .main */
                    <div className={styles.chatColumn} {...getRootProps()}>
                        <input {...getInputProps()} />
                        <ThreadPanel
                            key={`${currentListingId}-${currentOtherUserId}`}
                            listingId={currentListingId}
                            otherUserId={currentOtherUserId}
                            api={api}
                            myId={myId}
                            onBack={() => navigate("/messages")}
                            onListingLoad={setActiveListing}
                            onConversationMessage={handleConversationMessage}
                            onConversationRead={handleConversationRead}
                            dropRef={threadDropRef}
                            isDragActive={isDragActive}
                        />
                    </div>
                ) : (
                    /* No conversation selected */
                    <div className={styles.emptyPane}>
                        <span>💬</span>
                        <p>{t("messages.selectConversation")}</p>
                    </div>
                )}
            </div>

        </div>
    );
}